import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Avatar,
  ClickAwayListener,
  Typography,
  useTheme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";
import { getBackgroundColor } from "../utils";

interface Option {
  _id: string;
  fullName?: string;
  user?: {
    _id: string;
    firstName: string;
    lastName: string;
    [key: string]: any;
  };
  [key: string]: any;
}

// Helper function to get display name from option
const getDisplayName = (option: Option): string => {
  if (option.user?.firstName || option.user?.lastName) {
    return `${option.user?.firstName || ''} ${option.user?.lastName || ''}`.trim();
  }
  return option.fullName || '';
};

// Helper function to get user ID from option
const getUserId = (option: Option): string => {
  return option.user?._id || option._id;
};

interface CustomAutocompleteProps {
  options: Option[];
  value: Option[];
  onChange: (values: Option[]) => void;
  placeholder: string;
  label: string;
  disabled?: boolean;
  multiple?: boolean;
  isAssignee?: boolean;
  projectId?: string;
}

const CustomAutocomplete: React.FC<CustomAutocompleteProps> = ({
  options,
  value,
  onChange,
  placeholder,
  label,
  disabled = false,
  multiple = true,
  isAssignee = false,
  projectId,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update filtered options when input changes
  useEffect(() => {
    if (inputValue.trim() === "") {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) =>
        getDisplayName(option).toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [inputValue, options]);

  // Handle clicking outside to close dropdown
  const handleClickAway = () => {
    setOpen(false);
    setInputValue("");
  };

  // Handle option selection
  const handleOptionClick = (option: Option) => {
    if (multiple) {
      const isSelected = value.some((item) => getUserId(item) === getUserId(option));
      if (isSelected) {
        onChange(value.filter((item) => getUserId(item) !== getUserId(option)));
      } else {
        onChange([...value, option]);
      }
    } else {
      onChange([option]);
      setOpen(false);
    }
    setInputValue("");
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle chip removal (for multiple selection)
  const handleDeleteChip = (optionToDelete: Option) => {
    onChange(value.filter((option) => getUserId(option) !== getUserId(optionToDelete)));
  };

  // Handle input focus
  const handleFocus = () => {
    setOpen((prev) => !prev);
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setOpen(true);
  };

  // Handle key presses
  // const handleKeyDown = (e: React.KeyboardEvent) => {
  //   if (e.key === "Escape") {
  //     setOpen(false);
  //     setInputValue("");
  //     e.stopPropagation();
  //   } else if (e.key === "ArrowDown" && !open) {
  //     setOpen(true);
  //   } else if (e.key === "Tab" && open) {
  //     setOpen(false);
  //   }
  // };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: "relative", width: "100%" }} className="custom-autocomplete-root">
        <Box
          className="custom-autocomplete-input-root"
          sx={{
            position: "relative",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            minHeight: "40px",
            borderRadius: "4px",
            padding: "6px",
            paddingTop: "10px",
            cursor: disabled ? "not-allowed" : "text",
            backgroundColor: disabled
              ? theme.palette.action.disabledBackground
              : "inherit",
            "&:hover fieldset": {
              borderColor: !disabled
                ? theme.palette.action.active
                : theme.palette.divider,
            },
            "&:focus-within fieldset": {
              borderColor: theme.palette.primary.main,
              borderWidth: "2px",
            },
            "&:focus-within .MuiInputLabel-root": {
              color: theme.palette.primary.main,
            },
          }}
          onClick={() => {
            if (!disabled && inputRef.current) {
              inputRef.current.focus();
            }
          }}
        >
          <Typography
            className="MuiInputLabel-root"
            sx={{
              position: "absolute",
              top: "-9px",
              left: "10px",
              backgroundColor: theme.palette.background.paper,
              px: 0.5,
              fontSize: "12px",
              color: theme.palette.text.secondary,
              transition: "color 0.2s",
            }}
          >
            {label}
          </Typography>
          <fieldset
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: "-5px 0 0 0",
              margin: 0,
              padding: "0 8px",
              pointerEvents: "none",
              borderRadius: "4px",
              borderStyle: "solid",
              borderWidth: "1px",
              borderColor: theme.palette.divider,
              overflow: "hidden",
            }}
          >
            <legend
              style={{
                float: "unset",
                width: "auto",
                overflow: "hidden",
                display: "block",
                padding: 0,
                height: "11px",
                fontSize: "0.75em",
                visibility: "hidden",
                maxWidth: "100%",
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ paddingLeft: "5px", paddingRight: "5px" }}>
                {label}
              </span>
            </legend>
          </fieldset>
          {multiple &&
            value.map((option) => (
              <Chip
                key={getUserId(option)}
                label={getDisplayName(option)}
                size="small"
                avatar={
                  <Avatar
                    sx={{
                      bgcolor: isAssignee ? getBackgroundColor(
                        option?.user?._id || option._id,
                        projectId,
                      ) : theme.palette.primary.main,
                      color: "#ffffff !important",
                      width: 20,
                      height: 20,
                      fontSize: "10px",
                    }}
                  >
                    {getDisplayName(option)?.charAt(0)}
                  </Avatar>
                }
                onDelete={() => handleDeleteChip(option)}
                sx={{
                  borderRadius: "4px",
                  height: "24px",
                  margin: "1px",
                  "& .MuiChip-label": {
                    fontSize: "11px",
                    px: 0.5,
                  },
                  "& .MuiChip-avatar": {
                    width: 20,
                    height: 20,
                    marginLeft: "4px",
                    marginRight: "2px",
                  },
                }}
                disabled={disabled}
              />
            ))}

          {!multiple && value.length > 0 && (
            <Typography variant="body2">{getDisplayName(value[0])}</Typography>
          )}

          <input
            type="text"
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onClick={handleFocus}
            placeholder={value.length === 0 ? placeholder : ""}
            style={{
              flex: "1 1 auto",
              minWidth: "50px",
              border: "none",
              outline: "none",
              padding: "2px 4px",
              background: "transparent",
              fontSize: "14px",
              cursor: disabled ? "not-allowed" : "text",
            }}
            disabled={disabled}
          />
        </Box>

        {open && !disabled && (
          <Paper
            ref={dropdownRef}
            sx={{
              position: "absolute",
              width: "100%",
              mt: 0.5,
              zIndex: 9999,
              maxHeight: "250px",
              overflowY: "auto",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            }}
          >
            <List dense sx={{ padding: 0 }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const isSelected = value.some(
                    (item) => getUserId(item) === getUserId(option)
                  );
                  return (
                    <ListItem
                      key={getUserId(option)}
                      onClick={() => handleOptionClick(option)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: isSelected
                          ? theme.palette.action.selected
                          : "inherit",
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 20,
                            height: 20,
                            fontSize: "12px",
                            mr: 1,
                            bgcolor: isAssignee ? getBackgroundColor(
                              option?.user?._id || option._id,
                              projectId,
                            ) : theme.palette.primary.main,
                            color: "#ffffff !important",
                          }}
                        >
                          {getDisplayName(option)?.charAt(0)}
                        </Avatar>
                        <Typography sx={{ flexGrow: 1, fontSize: "13px" }}>
                          {getDisplayName(option)}
                        </Typography>
                        {isSelected && (
                          <CheckIcon
                            sx={{
                              color: theme.palette.primary.main,
                              ml: 1,
                              fontSize: "18px",
                            }}
                          />
                        )}
                      </Box>
                    </ListItem>
                  );
                })
              ) : (
                <ListItem>
                  <ListItemText primary={t("No options found")} />
                </ListItem>
              )}
            </List>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default CustomAutocomplete;